import { useEffect, useRef } from 'react';

import {
  AUTO_FORWARD_ENABLED_KEY,
  FORWARD_MODE_KEY,
  PROXY_URL_KEY,
  TARGET_URL_KEY,
  type ForwardMode,
} from '@/constant/eventForwardSamples';
import { forwardEvent, type ForwardConfig } from '@/lib/forwardEvent';
import { useEventExplorerStore } from '@/stores/useEventExplorerStore';

const MAX_SEEN_IDS = 5000;
const MAX_CONSECUTIVE_FAILURES = 10;

/** Read forwarding config from localStorage. Returns null if not configured. */
function readForwardConfig(): ForwardConfig | null {
  const mode = localStorage.getItem(FORWARD_MODE_KEY) as ForwardMode | null;
  const proxyUrl = localStorage.getItem(PROXY_URL_KEY) ?? '';
  const targetUrl = localStorage.getItem(TARGET_URL_KEY) ?? '';

  if (!targetUrl) return null;
  if ((mode === 'proxy' || !mode) && !proxyUrl) return null;

  return { mode: mode ?? 'proxy', proxyUrl, targetUrl };
}

/**
 * Auto-forwards new events as they arrive in the live buffer.
 *
 * Subscribes to liveBuffer changes in the Zustand store and forwards
 * only truly new events (not previously seen). Snapshots existing IDs
 * on enable to avoid replaying old events.
 */
export function useAutoForward() {
  const autoForwardEnabled = useEventExplorerStore((s) => s.autoForwardEnabled);
  const forwardedIdsRef = useRef(new Set<string>());
  const consecutiveFailuresRef = useRef(0);

  useEffect(() => {
    if (!autoForwardEnabled) {
      forwardedIdsRef.current.clear();
      consecutiveFailuresRef.current = 0;
      return;
    }

    // Snapshot current event IDs so we don't replay them
    const state = useEventExplorerStore.getState();
    for (const e of state.events) forwardedIdsRef.current.add(e.id);
    for (const e of state.liveBuffer) forwardedIdsRef.current.add(e.id);

    const unsub = useEventExplorerStore.subscribe(
      (s) => s.liveBuffer,
      (newBuffer) => {
        const config = readForwardConfig();
        if (!config) return;

        const newEvents = newBuffer.filter(
          (e) => !forwardedIdsRef.current.has(e.id)
        );

        for (const event of newEvents) {
          forwardedIdsRef.current.add(event.id);

          // Cap seen IDs to prevent memory growth (mutate in place)
          if (forwardedIdsRef.current.size > MAX_SEEN_IDS) {
            const ids = Array.from(forwardedIdsRef.current);
            const toRemove = ids.slice(0, ids.length - MAX_SEEN_IDS);
            for (const id of toRemove) forwardedIdsRef.current.delete(id);
          }

          forwardEvent(event, config).then(({ error }) => {
            const store = useEventExplorerStore.getState();
            store.incrementAutoForwardStats(!error);

            if (error) {
              consecutiveFailuresRef.current++;
              if (consecutiveFailuresRef.current >= MAX_CONSECUTIVE_FAILURES) {
                store.setAutoForwardEnabled(false);
                localStorage.setItem(AUTO_FORWARD_ENABLED_KEY, 'false');
              }
            } else {
              consecutiveFailuresRef.current = 0;
            }
          });
        }
      }
    );

    return () => {
      unsub();
    };
  }, [autoForwardEnabled]);
}
